import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import {
  doc, getDoc, setDoc, updateDoc,
} from 'firebase/firestore';
import { auth, db } from '../firebase';

export const SUPER_ADMIN_EMAIL = 'admin@ps.com';

const AuthContext = createContext(null);

async function getOrCreateAdminProfile(user) {
  const ref = doc(db, 'admin_profiles', user.uid);
  const snap = await getDoc(ref);
  const isSuperAdmin = user.email.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();

  if (snap.exists()) {
    const data = snap.data();
    // Self-heal super admin profile if it ended up in a non-approved state
    if (isSuperAdmin && (data.status !== 'approved' || !data.is_super_admin)) {
      const fixed = {
        ...data,
        status: 'approved',
        is_super_admin: true,
        approved_at: data.approved_at || new Date().toISOString(),
      };
      await updateDoc(ref, { status: 'approved', is_super_admin: true });
      return fixed;
    }
    return data;
  }

  // First time login — auto approve super admin, pend everyone else
  const profile = {
    uid: user.uid,
    email: user.email,
    status: isSuperAdmin ? 'approved' : 'pending',
    is_super_admin: isSuperAdmin,
    created_at: new Date().toISOString(),
    approved_at: isSuperAdmin ? new Date().toISOString() : null,
    approved_by_uid: null,
    approved_by_email: null,
  };
  await setDoc(ref, profile);
  return profile;
}

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [adminProfile, setAdminProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setAdmin(null);
        setAdminProfile(null);
        setLoading(false);
        return;
      }

      try {
        const profile = await getOrCreateAdminProfile(user);

        if (profile.status !== 'approved') {
          // Not approved — sign out silently; login() will surface the error
          await signOut(auth);
          setAdmin(null);
          setAdminProfile(null);
          setLoading(false);
          return;
        }

        setAdmin({ uid: user.uid, email: user.email, username: user.email });
        setAdminProfile(profile);
      } catch (err) {
        console.error('AuthContext: failed to load admin profile', err);
        setAdmin(null);
        setAdminProfile(null);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, []);

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const profile = await getOrCreateAdminProfile(cred.user);

    if (profile.status === 'pending') {
      await signOut(auth);
      throw new Error('Your account is pending super admin approval. Please wait for approval before logging in.');
    }
    if (profile.status === 'revoked') {
      await signOut(auth);
      throw new Error('Your account has been revoked. Please contact the super admin.');
    }
    return cred;
  };

  const register = async (email, password) => {
    if (email === SUPER_ADMIN_EMAIL) {
      throw new Error('This email address is reserved.');
    }

    // Check if profile already exists (re-registration attempt)
    const existingCred = await createUserWithEmailAndPassword(auth, email, password);
    const profile = {
      uid: existingCred.user.uid,
      email,
      status: 'pending',
      is_super_admin: false,
      created_at: new Date().toISOString(),
      approved_at: null,
      approved_by_uid: null,
      approved_by_email: null,
    };
    await setDoc(doc(db, 'admin_profiles', existingCred.user.uid), profile);

    // Sign out immediately — must wait for approval
    await signOut(auth);
    return { pendingApproval: true };
  };

  const logout = () => signOut(auth);

  const sendResetEmail = (email) => sendPasswordResetEmail(auth, email);

  const changeOwnPassword = async (currentPassword, newPassword) => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  };

  const isSuperAdmin = adminProfile?.is_super_admin === true;

  if (loading) return null;

  return (
    <AuthContext.Provider value={{
      admin,
      adminProfile,
      isSuperAdmin,
      login,
      register,
      logout,
      sendResetEmail,
      changeOwnPassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
